import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { queryCoachProfilesByUserId } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

type Params = { coach_user_id: string };

export type CoachingCoachesCoachModel = {
  publishedProfile?: CoachProfile;
  pendingProfile?: CoachProfile;
};

export default class CoachingCoachesCoachRoute extends Route<CoachingCoachesCoachModel> {
  @service declare store: Store;

  #scrollResetFrame: number | null = null;

  activate() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Reset on the next frame, not synchronously: activate() runs before the
    // detail template paints, so scrolling now resets the still-visible list
    // page and the detail then shows at the scroll offset inherited from it
    // (near the bottom) before snapping up. A rAF lands once the detail is in
    // the DOM, so it opens at the top with no visible jump.
    this.#scrollResetFrame = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }

  deactivate() {
    if (this.#scrollResetFrame !== null) {
      cancelAnimationFrame(this.#scrollResetFrame);
      this.#scrollResetFrame = null;
    }

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }
  }

  async model(params: Params) {
    const userProfiles =
      (
        await this.store.request(
          queryCoachProfilesByUserId(params.coach_user_id)
        )
      )?.content.data ?? [];

    return {
      publishedProfile: userProfiles.find(
        (profile) => profile.status === 'published'
      ),
      pendingProfile: userProfiles.find(
        (profile) => profile.status === 'pending_review'
      ),
    };
  }
}
